import React, { useState } from 'react';
import { Alert, Button, Modal, Stack, Tab, Tabs } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AbsencePeriod,
  ConvoUserView,
  Event,
  InstitutionGroup,
  InstitutionGroupAttachment,
  InstitutionGroupEventView,
  InstitutionGroupEventViewRow,
  InstitutionGroupMember,
  InstitutionGroupMemberView,
  InstitutionGroupMemberViewRow,
  InstitutionGroupTimeLabel,
  ReportInstitutionGroupView,
  TaskGroupView,
  TaskUserGroupView,
  VisibilityLimitOptions,
} from 'cb-model';
import {
  addControlColumn,
  CalendarView,
  CalendarViewEvent,
  ColourKey,
  ColourKeyItem,
  Display,
  IsAllowed,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  useCacheQuery,
  useCalendarRange,
  useCacheRecord,
  Uneditable,
} from 'gossamer-front-end';
import { displayLocaleTime, getFilter, getListRowLink } from 'gossamer-unified';
import { AddMemberToGroupButton } from './AddMemberToGroupButton';
import { AddEventToGroupButton } from './AddEventToGroupButton';
import { RemoveMemberFromGroupButton } from './RemoveMemberFromGroupButton';
import { RemoveEventFromGroupButton } from './RemoveEventFromGroupButton';
import { CreateForumConvoModal } from '../convo/CreateForumConvoModal';
import { SummaryChartTable, useDateRange } from '../reporting';
import { AllEventsAddCoHostButton } from './AllEventsAddCoHostButton';
import { AllEventsChangeHostButton } from './AllEventsChangeHostButton';
import { FeedbackDateRangeExport } from '../feedback-form/FeedbackDateRangeExport';
import { InstitutionGroupBookingMatrix } from './InstitutionGroupBookingMatrix';
import { GroupDeactivateButton } from './GroupDeactivateButton';
import { GroupDeleteEventsButton } from './GroupDeleteEventsButton';

interface GroupCalendarEvent extends CalendarViewEvent {
  colorCode: string;
}

const GROUP_EVENT_COLORS = {
  public: '#2563EB',
  private: '#7C3AED',
  'private-group': '#0D9488',
  closed: '#475569',
  cancelled: '#DC2626',
};

const GROUP_EVENT_LEGEND: ColourKeyItem[] = VisibilityLimitOptions.map((option) => ({
  id: option.id,
  label: option.label,
  bgColor: GROUP_EVENT_COLORS[option.id as keyof typeof GROUP_EVENT_COLORS] || GROUP_EVENT_COLORS.private,
}));

export const InstitutionGroupDisplay = (): React.JSX.Element => {
  const params = useParams();
  const record = useCacheRecord(InstitutionGroup, params.key);
  const [searchParams, setSearchParams] = useSearchParams();
  const reportingDateRange = useDateRange();
  const reportingQuery = useCacheQuery(ReportInstitutionGroupView, {
    where: {
      and: [
        getFilter('institution_group_id', 'eq', params.key),
        getFilter('week_commencing', 'gt', reportingDateRange.start.toISOString()),
        getFilter('week_commencing', 'lt', reportingDateRange.end.toISOString()),
      ],
    },
    order: [['week_commencing', 'ASC']],
  });

  return (
    <>
      <NavIconContainer entityId={InstitutionGroup.id} />
      <PageTitle
        verb="Display"
        entity={InstitutionGroup}
        keyValue={params.key}
        subtitle="Details relating to this Group"
      />

      <Tabs
        activeKey={searchParams.get('tab') || 'overview'}
        className="mb-3"
        variant="underline"
        mountOnEnter={true}
        unmountOnExit={true}
        onSelect={(tab) => {
          const newParams = new URLSearchParams();
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="overview" title="Overview">
          <Section>
            <SummaryChartTable
              data={reportingQuery.data || []}
              entity={ReportInstitutionGroupView}
              period={reportingDateRange}
            />
          </Section>

          <Section>
            <QuerySingle entity={InstitutionGroup} keyValue={params.key} render={Display} />
            <Stack direction="horizontal" gap={2}>
              <IsAllowed entity={InstitutionGroup} action="update">
                <Link to={`/admin/institution-group/update/${params.key}`}>
                  <Button size="sm" variant="primary">
                    Update
                  </Button>
                </Link>
              </IsAllowed>
              <IsAllowed entity={InstitutionGroup} action="delete">
                <GroupDeactivateButton institutionGroupId={parseInt(params.key, 10)} />
              </IsAllowed>
            </Stack>
          </Section>
          <Section title="Absence Periods" subtitle="Defined time periods for tracking member absences and attendance">
            <QueryMulti
              entity={AbsencePeriod}
              alterEntity={(localEntity: typeof AbsencePeriod) => {
                localEntity.fields.institution_group_id.listColumn = false;
                localEntity.fields.institution_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
                order: [['start_date', 'DESC']],
              }}
              render={List}
            />
            <IsAllowed entity={AbsencePeriod} action="create">
              <Link to={`/admin/absence-period/create`}>
                <Button size="sm" variant="primary">
                  Create New
                </Button>
              </Link>
            </IsAllowed>
          </Section>

          {!!params.key && (
            <Section title="Export Feedback">
              <FeedbackDateRangeExport
                endpointPath={`/main/feedback/export/institution-group/${encodeURIComponent(params.key)}`}
              />
            </Section>
          )}

          {/* <Section title="Rotations" subtitle="A sequence of time-periods into which this group is divided">
            <QueryMulti
              entity={InstitutionGroupTimeLabel}
              alterEntity={(localEntity: typeof InstitutionGroupTimeLabel) => {
                localEntity.fields.institution_group_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
            <IsAllowed entity={InstitutionGroupTimeLabel} action="update">
              <Link to={`/admin/institution-group/time-labels-grid/${params.key}`}>
                <Button size="sm" variant="primary">
                  Update
                </Button>
              </Link>
            </IsAllowed>
          </Section> */}

          <Section title="Attachments" subtitle="Files made available to members in the group">
            <QueryMulti
              entity={InstitutionGroupAttachment}
              alterEntity={(localEntity: typeof InstitutionGroupAttachment) => {
                localEntity.fields.institution_group_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
            <IsAllowed entity={InstitutionGroupAttachment} action="update">
              <Link to={`/admin/institution-group/attachments-grid/${params.key}`}>
                <Button size="sm" variant="primary">
                  Update
                </Button>
              </Link>
            </IsAllowed>
          </Section>

          <Section title="Conversations" subtitle="Message threads available to members in this group">
            <QueryMulti
              entity={ConvoUserView}
              queryOptions={{
                where: {
                  and: [getFilter('forum_type', 'eq', 'InstitutionGroup'), getFilter('forum_key', 'eq', params.key)],
                },
              }}
              render={List}
            />
            {record.data && (
              <CreateForumConvoModal
                forumType="InstitutionGroup"
                forumKey={params.key}
                institutionId={record.data.institution_id}
              />
            )}
          </Section>
        </Tab>

        <Tab eventKey="members" title="Members">
          <Section title="Members" subtitle="Most recently joined members">
            <QueryMulti
              entity={InstitutionGroupMemberView}
              alterEntity={(localEntity: typeof InstitutionGroupMemberView) => {
                localEntity.fields.institution_group_id.listColumn = false;
                localEntity.fields.institution_id.listColumn = false;
                addControlColumn(
                  localEntity,
                  'remove',
                  999,
                  (row: InstitutionGroupMemberViewRow) => String(row.id),
                  RemoveMemberFromGroupButton,
                );
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
                order: [['created_at', 'DESC']],
              }}
              render={List}
            />
            <IsAllowed entity={InstitutionGroupMember} action="update">
              <div className="mb20px" />
              {!!record.data && (
                <AddMemberToGroupButton
                  institutionGroupId={parseInt(params.key)}
                  institutionId={record.data.institution_id}
                />
              )}
              <div className="mb20px" />
              <Link to={`/admin/institution-group/bulk-upload-members/${params.key}`}>
                <Button size="sm" variant="primary">
                  Bulk Upload Members to Add
                </Button>
              </Link>
            </IsAllowed>
          </Section>

          <Section title="Task Progress Summary" subtitle="How this group's members are getting on with their tasks">
            <QuerySingle
              entity={TaskGroupView}
              keyValue={params.key}
              render={Display}
              fieldGroup="summary"
              renderNotFound={() => <div>No tasks</div>}
            />
          </Section>
          <Section title="Task Progress by Member" subtitle="How these members are getting on with their tasks">
            <QueryMulti
              entity={TaskUserGroupView}
              alterEntity={(localEntity: typeof TaskUserGroupView) => {
                localEntity.fields.institution_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
          </Section>
        </Tab>

        <Tab eventKey="events" title="Events">
          <Section title="Group Events" subtitle="Events happening for learners of this group">
            <EventCalendar institution_group_id={parseInt(params.key)} />
            <IsAllowed entity={InstitutionGroupMember} action="update">
              <div className="mb20px" />
              {!!record.data && (
                <Stack direction="vertical" gap={3}>
                  <AddEventToGroupButton
                    institutionGroupId={parseInt(params.key)}
                    institutionId={record.data.institution_id}
                  />
                  <AllEventsChangeHostButton institutionGroupId={parseInt(params.key)} />
                  <AllEventsAddCoHostButton institutionGroupId={parseInt(params.key)} />
                  <Stack direction="horizontal" gap={3}>
                    <Link to={`/admin/institution-group/event-bulk-copy/${params.key}`}>
                      <Button size="sm" variant="primary">
                        Bulk Copy Events into this Group
                      </Button>
                    </Link>
                    <GroupDeleteEventsButton institutionGroupId={parseInt(params.key)} />
                  </Stack>
                </Stack>
              )}
            </IsAllowed>
          </Section>
        </Tab>
        <Tab eventKey="bookings" title="Bookings">
          <Section
            title="Group Booking Matrix"
            subtitle="Booking state for every event and member currently linked to this group"
          >
            {!!params.key && <InstitutionGroupBookingMatrix institutionGroupId={Number(params.key)} />}
          </Section>
        </Tab>
      </Tabs>
    </>
  );
};

const EventCalendar = (props: { institution_group_id: number }): React.JSX.Element => {
  const [showingEvent, setShowingEvent] = useState<GroupCalendarEvent | null>(null);
  const { range } = useCalendarRange();
  const navigate = useNavigate();
  const [stableEventRows, setStableEventRows] = React.useState<InstitutionGroupEventViewRow[]>([]);
  const eventQuery = useCacheQuery(InstitutionGroupEventView, {
    where: {
      and: [
        getFilter('institution_group_id', 'eq', String(props.institution_group_id)),
        getFilter('start_at', 'gt', range.start.toISOString()),
        getFilter('start_at', 'lt', range.end.toISOString()),
      ],
    },
    limit: 1000,
  });
  React.useEffect(() => {
    if (eventQuery.data !== undefined) {
      setStableEventRows(eventQuery.data);
    }
  }, [eventQuery.data]);
  const calendarRows = eventQuery.data ?? stableEventRows;
  const events: GroupCalendarEvent[] = calendarRows.map((e) => ({
    id: String(e.id),
    title: e.title || 'Event',
    start: new Date(e.start_at),
    end: new Date(e.end_at),
    colorCode:
      e.visibility_limit === 'cancelled'
        ? GROUP_EVENT_COLORS.cancelled
        : GROUP_EVENT_COLORS[e.visibility_limit as keyof typeof GROUP_EVENT_COLORS] || GROUP_EVENT_COLORS.private,
  }));
  return (
    <>
      <CalendarView<GroupCalendarEvent>
        events={events}
        handleEventSelect={(event) => setShowingEvent(event)}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.colorCode,
            borderColor: event.colorCode,
            color: '#FFFFFF',
          },
        })}
      />
      <ColourKey items={GROUP_EVENT_LEGEND} />
      <Modal
        show={!!showingEvent}
        onHide={() => setShowingEvent(null)}
        aria-labelledby="group-event-modal-title"
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="group-event-modal-title" as="h5">
            {showingEvent?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack direction="horizontal" gap={2}>
            <Uneditable field={InstitutionGroupEventView.fields.start_at} value={showingEvent?.start?.toISOString()} />
            -&nbsp;
            {showingEvent?.end ? displayLocaleTime(showingEvent?.end) : 'N/A'}
          </Stack>
          <br />
          <Stack direction="horizontal" gap={2}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const eventId = calendarRows.find((e) => String(e.id) === showingEvent?.id)?.event_id;
                if (!eventId) {
                  return;
                }
                navigate(getListRowLink(Event.listRowLink, String(eventId)));
              }}
            >
              Go to Event
            </Button>
            <RemoveEventFromGroupButton value={showingEvent?.id} handleSuccess={() => setShowingEvent(null)} />
          </Stack>
        </Modal.Body>
      </Modal>
    </>
  );
};
